// src/pages/ventas/Ventas.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useState, useEffect, useContext } from "react";
import {
  Grid,
  FormControl,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Paper,
  Typography,
  Alert,
  Fade,
  Container,
  Stack,
  alpha,
} from "@mui/material";
import PageLayout from "../../components/layout/PageLayout";
import {
  Storefront as StorefrontIcon, LocationOn,
  PersonSearch, ShoppingCart, Payment as PaymentIcon,
  CheckCircle, WarningAmber, Info,
} from "@mui/icons-material";
import { BuscadorClientes } from "../../components/module/ventas/BuscadorClientes";
import { BuscadorArticulos } from "../../components/module/ventas/BuscadorArticulos";
import { CarritoVenta } from "../../components/module/ventas/CarritoVenta";
import { ResumenVenta } from "../../components/module/ventas/ResumenVenta";
import { useAuth } from "../../context/AuthContext";
import { useAuthorization } from "../../hooks/useAuthorization";
import Swal from "sweetalert2";
import { toast } from "../../utils/toast";
import { VentasApi } from "../../services/api/VentasApi";
import axios from "axios";
import { useFacturacionDisponible } from "../../hooks/useFacturacionDisponible";
import { useLocation } from "react-router-dom";
import PaymentModal from "../../components/module/ventas/PaymentModal";
import { DarkModeContext } from "../../context/darkModeContext";

// ── Interfaces ──
interface Sucursal { Id: number; Nombre: string }
interface ArticuloVenta {
  Id: number; Nombre: string; EAN: string; PrecioVenta: number;
  Cantidad: number; Descuento: number; Valor: number; stock: number;
  Impuesto: Array<{ Id: number; Nombre: string; Valor: number }>;
}
interface VentaState {
  id?: number;
  cliente?: { id: number; nombre: string };
  articulos: ArticuloVenta[];
  subtotal: number; descuentoTotal: number;
  impuestos: Array<{ nombre: string; valor: number }>;
  totalAPagar: number; numeroFactura?: string;
}

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4020";

// ── Paleta Notion/Linear ──
const P = {
  light: {
    bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F",
    textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", borderH: "#D3D3D0",
    accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C", successBg: "#DBEDDB",
    warning: "#D9730D", warningBg: "#FADEC9", error: "#E03E3E", errorBg: "#FBE4E4",
    shadow: "0 1px 3px rgba(0,0,0,0.04)", hover: "rgba(55,53,47,0.04)",
  },
  dark: {
    bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6",
    textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", borderH: "#444444",
    accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A", successBg: "#1A2F2A",
    warning: "#C77D48", warningBg: "#2E2215", error: "#D44C47", errorBg: "#3D2020",
    shadow: "0 1px 3px rgba(0,0,0,0.2)", hover: "rgba(255,255,255,0.04)",
  },
};

// ── Section Card ──
const SectionCard: React.FC<{
  icon: React.ReactElement; title: string; subtitle?: string;
  children: React.ReactNode; done?: boolean; active?: boolean;
  darkMode: boolean;
}> = ({ icon, title, subtitle, children, done, active = true, darkMode }) => {
  const p = darkMode ? P.dark : P.light;
  return (
    <Paper elevation={0} sx={{
      p: 3, mb: 2.5, borderRadius: "12px",
      border: `1px solid ${done ? p.success : active ? p.border : p.border}`,
      bgcolor: p.bg, boxShadow: p.shadow,
      opacity: active ? 1 : 0.5, transition: "all 0.2s",
    }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Box sx={{
          width: 34, height: 34, borderRadius: "8px",
          bgcolor: done ? p.successBg : p.accentBg,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: done ? p.success : p.accent,
          "& svg": { fontSize: 17 },
        }}>
          {done ? <CheckCircle /> : icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 650, color: p.text, lineHeight: 1.3 }}>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: "0.75rem", color: p.textT, lineHeight: 1.3 }}>{subtitle}</Typography>}
        </Box>
        {done && (
          <Box sx={{ px: 1.2, py: 0.3, borderRadius: "6px", bgcolor: p.successBg, color: p.success, fontSize: "0.7rem", fontWeight: 600 }}>
            Listo
          </Box>
        )}
      </Stack>
      {children}
    </Paper>
  );
};

// ── Progress Steps ──
const ProgressSteps: React.FC<{ step: number; darkMode: boolean }> = ({ step, darkMode }) => {
  const p = darkMode ? P.dark : P.light;
  const steps = [
    { label: "Sucursal", icon: <LocationOn sx={{ fontSize: 14 }} /> },
    { label: "Cliente", icon: <PersonSearch sx={{ fontSize: 14 }} /> },
    { label: "Artículos", icon: <ShoppingCart sx={{ fontSize: 14 }} /> },
    { label: "Pago", icon: <PaymentIcon sx={{ fontSize: 14 }} /> },
  ];

  return (
    <Stack direction="row" alignItems="center" spacing={0} sx={{ mb: 3 }}>
      {steps.map((s, i) => {
        const done = i < step;
        const active = i === step;
        return (
          <React.Fragment key={s.label}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{
              px: 1.5, py: 0.8, borderRadius: "8px",
              bgcolor: done ? p.successBg : active ? p.accentBg : "transparent",
              transition: "all 0.2s",
            }}>
              <Box sx={{
                width: 22, height: 22, borderRadius: "6px",
                bgcolor: done ? p.success : active ? p.accent : p.bgT,
                color: done || active ? "#FFF" : p.textT,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "0.65rem", fontWeight: 700,
              }}>
                {done ? <CheckCircle sx={{ fontSize: 13 }} /> : s.icon}
              </Box>
              <Typography sx={{
                fontSize: "0.75rem", fontWeight: done || active ? 600 : 400,
                color: done ? p.success : active ? p.accent : p.textT,
                display: { xs: "none", sm: "block" },
              }}>
                {s.label}
              </Typography>
            </Stack>
            {i < steps.length - 1 && (
              <Box sx={{
                flex: 1, height: 2, mx: 0.5,
                bgcolor: i < step ? p.success : p.border,
                borderRadius: 1, transition: "background 0.3s",
              }} />
            )}
          </React.Fragment>
        );
      })}
    </Stack>
  );
};

// ── MAIN ──
const Ventas = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { requestAuthorization, AuthorizationComponent } = useAuthorization();
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  const [usarFacturaOficial, setUsarFacturaOficial] = useState(true);
  const { disponible, loading: loadingDisp } = useFacturacionDisponible();
  const holdDurationMs = parseInt(process.env.REACT_APP_TEMP_MODE_HOLD_MS || '3000', 10);

  const [ventaActual, setVentaActual] = useState<VentaState>({
    articulos: [], subtotal: 0, descuentoTotal: 0, impuestos: [], totalAPagar: 0,
  });
  const [ventaIniciada, setVentaIniciada] = useState(false);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [sucursalSel, setSucursalSel] = useState("");
  const [loadingSuc, setLoadingSuc] = useState(true);
  const [mostrarPago, setMostrarPago] = useState(false);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const calcularTotales = (arts: ArticuloVenta[]) => {
    const subtotal = arts.reduce((t, a) => t + a.PrecioVenta * a.Cantidad, 0);
    const descuentoTotal = arts.reduce((t, a) => {
      const sub = a.PrecioVenta * a.Cantidad;
      return t + sub * ((a.Descuento || 0) / 100);
    }, 0);
    const subConDesc = subtotal - descuentoTotal;
    const impuestos = arts.reduce((acc, a) => {
      const sub = a.PrecioVenta * a.Cantidad;
      const desc = sub * ((a.Descuento || 0) / 100);
      const subD = sub - desc;
      a.Impuesto?.forEach((imp) => {
        const ex = acc.find((i) => i.nombre === imp.Nombre);
        const val = subD * (imp.Valor / 100);
        if (ex) ex.valor += val; else acc.push({ nombre: imp.Nombre, valor: val });
      });
      return acc;
    }, [] as Array<{ nombre: string; valor: number }>);
    const totalImp = impuestos.reduce((t, i) => t + i.valor, 0);
    return { subtotal, descuentoTotal, impuestos, totalAPagar: subConDesc + totalImp };
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoadingSuc(true);
        const { data: suc } = await axios.post(`${API_URL}/administracion/sucursales/findAll`);
        setSucursales(suc);
        if (suc.length > 0) { setSucursalSel(suc[0].Id.toString()); setActiveStep(1); }

        const vp = location.state?.ventaPendiente;
        if (vp) {
          if (vp.IdEstadoVenta === 2) {
            const nf = vp.numeroFactura;
            if (nf) {
              const vc = await VentasApi.getVentaById(String(nf));
              if (vc) {
                setVentaActual({
                  id: vc.id, cliente: vc.cliente ? { id: Number(vc.cliente.identificacion) || 0, nombre: vc.cliente.nombre || "" } : undefined,
                  articulos: vc.articulos.map((a: any) => ({ ...a, stock: a.stock || 0 })),
                  subtotal: vc.subtotal || 0, descuentoTotal: vc.descuentoTotal || 0,
                  impuestos: vc.impuestos || [], totalAPagar: vc.totalAPagar || 0, numeroFactura: vc.numeroFactura,
                });
                setVentaIniciada(true); setMostrarPago(true); setActiveStep(3);
              }
            }
          } else if (vp.IdEstadoVenta === 1) {
            const arts = vp.articulos.map((a: any) => ({
              Id: a.Id, Nombre: a.Nombre, EAN: a.EAN, PrecioVenta: a.PrecioVenta,
              Cantidad: a.Cantidad, Descuento: a.Descuento || 0, Valor: a.PrecioVenta * a.Cantidad,
              stock: a.stock || 0, Impuesto: a.impuesto ? [{ Id: a.impuesto.Id, Nombre: a.impuesto.Nombre, Valor: a.impuesto.Valor }] : [],
            }));
            const totales = calcularTotales(arts);
            setVentaActual({
              id: vp.Id, cliente: { id: vp.IdCliente, nombre: vp.Cliente?.Nombre || "" },
              articulos: arts, ...totales,
            });
            setVentaIniciada(true); setActiveStep(2);
          }
        }
      } catch {
        toast.error("Error al inicializar");
      } finally { setLoadingSuc(false); }
    };
    init();
  }, [location.state]);

  const handleSucursalChange = (v: string) => {
    setSucursalSel(v);
    if (v && activeStep === 0) setActiveStep(1);
  };

  const handleClienteSeleccionado = async (cliente: { id: number; nombre: string }) => {
    if (!ventaIniciada && sucursalSel) {
      try {
        const data = await VentasApi.crearVenta(cliente.id, user?.Id || 0, new Date().toISOString());
        setVentaActual((prev) => ({ ...prev, id: data.id, cliente }));
        setVentaIniciada(true); setActiveStep(2);
        toast.success("Venta iniciada");
      } catch (e) { Swal.close(); toast.error((e as Error).message); }
    }
  };

  // `temporal` viene del gesto del boton (pulsacion sostenida), no del estado:
  // tomarlo de `usarFacturaOficial` justo despues de cambiarlo leia el valor anterior.
  const handleProcesarPago = async (temporal = false) => {
    const oficial = !temporal;
    try {
      Swal.fire({ title: "Procesando...", allowOutsideClick: false, showConfirmButton: false, didOpen: () => Swal.showLoading() });
      const r = await VentasApi.irAPagar({ idVenta: ventaActual.id || 0, idSucursal: Number(sucursalSel), usarFacturaOficial: oficial });
      if (r) {
        setUsarFacturaOficial(oficial);
        setVentaActual((prev) => ({ ...prev, numeroFactura: r.numeroFactura, totalAPagar: r.totalAPagar }));
        Swal.close(); setOpenPaymentModal(true); setMostrarPago(true); setActiveStep(3);
        if (temporal) toast.warning(`Factura temporal ${r.numeroFactura} — venta de prueba, no se emite ante la DIAN`);
      }
    } catch (e) { Swal.close(); toast.error((e as Error).message); }
  };

  const handlePagoCompletado = () => {
    setMostrarPago(false);
    Swal.close(); toast.success("¡Venta Completada!");
    setVentaActual({ articulos: [], subtotal: 0, descuentoTotal: 0, impuestos: [], totalAPagar: 0 });
    // Volver a factura oficial: si el modo temporal persistiera, la venta
    // siguiente saldria como prueba sin que nadie lo pidiera.
    setUsarFacturaOficial(true);
    setVentaIniciada(false); setMostrarPago(false); setActiveStep(1);
  };

  const handleCancelarVenta = async (tipo: "cancelacion" | "anulacion") => {
    if (!ventaActual.id) return;
    const r = await Swal.fire({
      title: tipo === "anulacion" ? "¿Anular Facturación?" : "¿Cancelar Venta?",
      text: "Esta acción no se puede deshacer", icon: "warning",
      showCancelButton: true, confirmButtonColor: p.error, confirmButtonText: "Sí", cancelButtonText: "No", reverseButtons: true,
    });
    if (!r.isConfirmed) return;

    // Solo pedir autorización para anulación (no para cancelación)
    let authCode: string | null = null;
    if (tipo === "anulacion") {
      authCode = await requestAuthorization("ANULAR_VENTA", "Anular Venta", {
        idVenta: ventaActual.id,
        codigoFactura: ventaActual.numeroFactura || "",
        fechaCreacionFactura: (ventaActual as any).fecha || "",
        cliente: ventaActual.cliente?.nombre || "",
      });
      if (authCode === null) return;
    }

    let observacion: string | undefined;
    if (tipo === "anulacion") {
      const result = await Swal.fire({
        title: "Motivo de anulación",
        input: "textarea",
        inputPlaceholder: "Ingrese el motivo o información relevante...",
        inputAttributes: { "aria-label": "Motivo de anulación" },
        showCancelButton: true,
        confirmButtonText: "Continuar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: p.error,
        reverseButtons: true,
        inputValidator: (value) => {
          if (!value?.trim()) return "Debe ingresar un motivo para continuar";
          return null;
        },
      });
      if (!result.value) return;
      observacion = result.value.trim();
    }

    Swal.fire({ title: "Procesando...", allowOutsideClick: false, showConfirmButton: false, willOpen: () => Swal.showLoading() });
    try {
      await VentasApi.cancelarVenta(ventaActual.id, tipo, observacion, authCode || undefined);
      setVentaActual({ articulos: [], subtotal: 0, descuentoTotal: 0, impuestos: [], totalAPagar: 0 });
      setVentaIniciada(false); setMostrarPago(false); setActiveStep(1);
      Swal.close(); toast.success(tipo === "anulacion" ? "Anulada" : "Cancelada");
    } catch (err: any) {
      Swal.close(); toast.error(err.message || "Ocurrió un error inesperado");
    }
  };

  const handleStockUpdated = (response: any) => {
    if (!response?.articulos) return;
    const arts = response.articulos.map((a: any) => {
      const ex = ventaActual.articulos.find((x) => x.Id === a.Id);
      return { ...a, Descuento: ex?.Descuento || a.Descuento || 0 };
    });
    const totales = calcularTotales(arts);
    setVentaActual((prev) => ({ ...prev, articulos: arts, ...totales }));
  };

  const handleArticuloSeleccionado = async (articulo: any, cantidad: number) => {
    if (!ventaActual.id || cantidad <= 0) return;
    if (articulo.stock < cantidad) { toast.warning(`Stock insuficiente: solo hay ${articulo.stock} unidades`); return; }
    try {
      Swal.fire({ title: "Agregando...", allowOutsideClick: false, showConfirmButton: false, didOpen: () => Swal.showLoading() });
      const r = await VentasApi.ajustarStock({ idVenta: ventaActual.id, idArticulo: articulo.Id, cantidad, accion: "AddAll" });
      const arts = r.articulos.map((a: any) => {
        const ex = ventaActual.articulos.find((x) => x.Id === a.Id);
        return { ...a, Descuento: ex?.Descuento || articulo.Descuento || a.Descuento || 0 };
      });
      handleStockUpdated({ ...r, articulos: arts });
      Swal.close(); toast.success(`Agregado: ${cantidad}x ${articulo.Nombre}`);
    } catch (e) { Swal.close(); toast.error((e as Error).message); }
  };

  const procesarPagoFinal = async (datoPago: { IdVenta: number; IdTipoPago: number; Abono: number; Observacion: string; CreditoInfo?: { NumeroCuotas: number; FechaPrimeraCuota: string; IntervaloDias: number } }) => {
    try {
      if (datoPago.Abono < ventaActual.totalAPagar) {
        const c = await Swal.fire({ title: "Pago Parcial", text: "La factura quedará pendiente", icon: "warning", showCancelButton: true, confirmButtonText: "Continuar", cancelButtonText: "Cancelar" });
        if (!c.isConfirmed) return;
      }
      Swal.fire({ title: "Procesando pago...", allowOutsideClick: false, showConfirmButton: false, didOpen: () => Swal.showLoading() });
      const r = await VentasApi.cobrarFactura(datoPago);
      setOpenPaymentModal(false); setMostrarPago(false);
      if (r.mensaje) { Swal.close(); toast.success(r.mensaje); setOpenPaymentModal(false); handlePagoCompletado(); }
    } catch (e) { Swal.close(); toast.error((e as Error).message); }
  };

  // ── Loading states ──
  if (loadingDisp) return (
    <PageLayout icon={StorefrontIcon} title="Nueva Venta">
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress size={32} sx={{ color: p.accent }} />
      </Box>
    </PageLayout>
  );

  if (location.state?.modo === "retomar" && location.state?.ventaPendiente?.IdEstadoVenta === 2 && !ventaActual.numeroFactura) return (
    <PageLayout icon={StorefrontIcon} title="Nueva Venta">
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box sx={{ p: 3, borderRadius: "12px", border: `1px solid ${p.warning}`, bgcolor: p.warningBg, textAlign: "center" }}>
          <WarningAmber sx={{ fontSize: 40, color: p.warning, mb: 1 }} />
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: p.warning, mb: 1 }}>Error al cargar venta</Typography>
          <Typography sx={{ fontSize: "0.84rem", color: p.textS }}>No se pudo cargar la venta pendiente. Intente desde el listado.</Typography>
        </Box>
      </Container>
    </PageLayout>
  );

  if (!disponible) return (
    <PageLayout icon={StorefrontIcon} title="Nueva Venta">
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box sx={{ p: 3, borderRadius: "12px", border: `1px solid ${p.error}`, bgcolor: p.errorBg, textAlign: "center" }}>
          <WarningAmber sx={{ fontSize: 40, color: p.error, mb: 1 }} />
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: p.error, mb: 1 }}>Facturación No Disponible</Typography>
          <Typography sx={{ fontSize: "0.84rem", color: p.textS }}>Contacte al administrador para renovar la configuración de facturas.</Typography>
        </Box>
      </Container>
    </PageLayout>
  );

  return (
    <PageLayout icon={StorefrontIcon} title="Nueva Venta">
        <Container maxWidth="xl" sx={{ py: 2 }}>
          {/* Progress */}
          <ProgressSteps step={activeStep} darkMode={darkMode} />

          <Grid container spacing={3}>
            {/* ── Left Column ── */}
            <Grid item xs={12} lg={8}>
              {/* Sucursal */}
              <SectionCard icon={<LocationOn />} title="Sucursal" subtitle="Punto de venta"
                done={!!sucursalSel} darkMode={darkMode}>
                <FormControl fullWidth size="small" disabled={ventaIniciada || loadingSuc}>
                  <Select value={sucursalSel} onChange={(e) => handleSucursalChange(e.target.value)} displayEmpty
                    sx={{
                      borderRadius: "8px", bgcolor: p.bgS, fontSize: "0.88rem",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: p.border },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: p.borderH },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: p.accent },
                    }}>
                    {sucursales.map((s) => (
                      <MenuItem key={s.Id} value={s.Id.toString()}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <LocationOn sx={{ fontSize: 16, color: p.textT }} />
                          <span>{s.Nombre}</span>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </SectionCard>

              {/* Cliente */}
              {!ventaIniciada && sucursalSel && (
                <Fade in timeout={300}>
                  <Box>
                    <SectionCard icon={<PersonSearch />} title="Cliente" subtitle="Seleccione para iniciar la venta"
                      done={false} darkMode={darkMode}>
                      <BuscadorClientes
                        onClienteSeleccionado={handleClienteSeleccionado}
                        disabled={ventaIniciada || !sucursalSel || loadingSuc}
                      />
                    </SectionCard>
                  </Box>
                </Fade>
              )}

              {/* Buscador Artículos */}
              {ventaIniciada && !mostrarPago && (
                <Fade in timeout={300}>
                  <Box>
                    <SectionCard icon={<ShoppingCart />} title="Agregar Artículos" subtitle="Busca por nombre o EAN"
                      done={false} darkMode={darkMode}>
                      <BuscadorArticulos
                        onArticuloSeleccionado={handleArticuloSeleccionado}
                        disabled={!ventaIniciada}
                        idSucursal={Number(sucursalSel)}
                      />
                    </SectionCard>
                  </Box>
                </Fade>
              )}

              {/* Carrito */}
              {ventaIniciada && (
                <Fade in timeout={400}>
                  <Box>
                    <Paper elevation={0} sx={{
                      p: 3, borderRadius: "12px", border: `1px solid ${p.border}`,
                      bgcolor: p.bg, boxShadow: p.shadow,
                    }}>
                      <CarritoVenta
                        idVenta={ventaActual.id || 0}
                        articulos={ventaActual.articulos}
                        onStockUpdated={handleStockUpdated}
                        showingPayment={mostrarPago}
                      />
                    </Paper>
                  </Box>
                </Fade>
              )}

              {/* Empty state */}
              {!sucursalSel && (
                <Box sx={{ p: 5, textAlign: "center", border: `2px dashed ${p.border}`, borderRadius: "12px", bgcolor: p.bgS }}>
                  <LocationOn sx={{ fontSize: 40, color: p.textT, opacity: 0.3, mb: 1 }} />
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: p.textS }}>Seleccione una sucursal para comenzar</Typography>
                </Box>
              )}
            </Grid>

            {/* ── Right Column: Resumen ── */}
            <Grid item xs={12} lg={4}>
              <Box sx={{ position: "sticky", top: 20 }}>
                <Paper elevation={0} sx={{
                  p: 3, borderRadius: "12px",
                  border: `1px solid ${p.accent}`,
                  bgcolor: p.bg, boxShadow: p.shadow,
                }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5, pb: 2, borderBottom: `1px solid ${p.border}` }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: "8px", bgcolor: p.accentBg,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: p.accent, "& svg": { fontSize: 16 },
                    }}>
                      <PaymentIcon />
                    </Box>
                    <Typography sx={{ fontSize: "0.9rem", fontWeight: 650, color: p.text }}>
                      Resumen de Venta
                    </Typography>
                  </Stack>

                  <ResumenVenta
                    venta={{
                      id: ventaActual.id || 0, cliente: ventaActual.cliente,
                      numeroFactura: ventaActual.numeroFactura,
                      subtotal: ventaActual.subtotal, descuentoTotal: ventaActual.descuentoTotal,
                      impuestos: ventaActual.impuestos, totalAPagar: ventaActual.totalAPagar,
                    }}
                    onProcesarPago={handleProcesarPago}
                    onCancelarVenta={handleCancelarVenta}
                    holdDurationMs={holdDurationMs}
                    disabled={!ventaIniciada || ventaActual.articulos.length === 0}
                    showingPayment={mostrarPago}
                    usarFacturaOficial={usarFacturaOficial}
                  />
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>

        <PaymentModal
          open={openPaymentModal} onClose={() => setOpenPaymentModal(false)}
          idVenta={ventaActual.id || 0} totalAPagar={ventaActual.totalAPagar}
          idSucursal={Number(sucursalSel)} cliente={ventaActual.cliente}
          numeroFactura={ventaActual.numeroFactura}
          onPagar={procesarPagoFinal} onPagoCompletado={handlePagoCompletado}
        />
        <AuthorizationComponent />
      </PageLayout>
  );
};

export default Ventas;
